import React, { useState, useRef } from 'react';
import { COVERED_RATIO } from '../../utils/reportColumns';
import { showsQuestionBreakdown, canSaveQuestionToLibrary, showsQuestionPoints } from './questionBreakdown';
import { observer } from 'mobx-react';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import MurphyInteractionWarning from '../Murphy/MurphyInteractionWarning';
import TranscriptSection from '../MyMeetings/TranscriptSection';

const NewAssessmentDetails = ({ assessments, onSaveToLibrary, canEditLibrary = true, existingQuestions = [], hideQuestionBreakdown = false, questionBreakdownOnly = false, initialAssessmentId = null, myMeetingsStore = null, companyId = null }) => {
    // If an initialAssessmentId is provided (e.g. deeplink from the meeting-ready
    // email's "View report" button), open on that assessment instead of the first one.
    const initiallySelected = (initialAssessmentId && assessments.find(a => a.id === initialAssessmentId)) || assessments[0];
    const [selectedAssessmentId, setSelectedAssessmentId] = useState(initiallySelected?.id);
    const [selectedSessionType, setSelectedSessionType] = useState(initiallySelected?.sessionType);
    const [savedQuestions, setSavedQuestions] = useState({});
    const questionBreakdownRef = useRef(null);
    const dateOptions = assessments.filter(assessment => assessment.sessionType === selectedSessionType).map((assessment, index) => ({
        key: index,
        text: new Date(assessment.completedDateTime).toLocaleDateString(),
        value: assessment.id,
    }));

    const sessionTypeOptions = assessments.reduce((collector, assessment, index) => {
        if (!collector.some(option => option.value === assessment.sessionType)) {
            collector.push({
                key: index,
                text: assessment.sessionType,
                value: assessment.sessionType
            })
        }
        return collector;
    }, [])

    const handleSessionTypeChange = (e) => {
        const value = e.target ? e.target.value : e;
        setSelectedSessionType(value);
        const firstAssessment = assessments.filter(assessment => assessment.sessionType === value)[0];
        setSelectedAssessmentId(firstAssessment?.id);
    }

    const selectedAssessment = assessments.find(a => a.id === selectedAssessmentId);

    if (!selectedAssessment) {
        return <div style={{ margin: 20, fontWeight: 'bold' }}>No assessments completed.</div>;
    }
    return (
        <>
            {!questionBreakdownOnly && selectedAssessment.aiGenerated && (
                <div
                    style={{
                        backgroundColor: 'rgb(7, 132, 167)',
                        padding: '16px',
                        textAlign: 'center',
                    }}
                >
                    <div style={{ color: 'white', margin: 0, fontFamily: '"Press Start 2P"', fontSize: '9px' }}>
                        &#x1F575;&#xFE0F; Review created by Murphy the MultiplyAI agent
                    </div>
                </div>
            )}
            <div style={{ padding: '20px', fontFamily: 'Montserrat, sans-serif' }}>
                {!questionBreakdownOnly && (
                  <>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '5px' }}>Type of Sales Message:</label>
                        <select
                            value={selectedSessionType}
                            onChange={handleSessionTypeChange}
                            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', minWidth: '180px' }}
                        >
                            {sessionTypeOptions.map(opt => (
                                <option key={opt.key} value={opt.value}>{opt.text}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '5px' }}>Date of Assessment:</label>
                        <select
                            value={selectedAssessmentId}
                            onChange={(e) => setSelectedAssessmentId(e.target.value)}
                            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', minWidth: '150px' }}
                        >
                            {dateOptions.map(opt => (
                                <option key={opt.key} value={opt.value}>{opt.text}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '18px' }}>
                        <div
                            data-testid="assessment-grade-1"
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                backgroundColor: { green: '#21ba45', yellow: '#fbbd08', red: '#db2828', grey: '#767676' }[selectedAssessment.grade] || '#767676',
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                            Attempts: <strong>{selectedAssessment.numberOfTries}</strong>
                        </span>
                        {selectedAssessment.sessionType !== 'Live Meeting' && (
                            <VideoPlayer videoKey={selectedAssessment.videoName} companyId={selectedAssessment.companyId} />
                        )}
                        {!hideQuestionBreakdown && showsQuestionBreakdown(selectedAssessment.sessionType) && selectedAssessment.qaAnalysis?.questionAnalysis?.length > 0 && (
                            <button
                                onClick={() => questionBreakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                style={{
                                    fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: 600,
                                    padding: '6px 14px', borderRadius: '6px', border: '1px solid #0784a7',
                                    backgroundColor: '#0784a7', color: '#fff', cursor: 'pointer',
                                    marginLeft: '8px', marginTop: '10px', height: '32px', whiteSpace: 'nowrap',
                                }}
                            >
                                Jump to Question Breakdown
                            </button>
                        )}
                    </div>
                </div>
                <MurphyInteractionWarning assessment={selectedAssessment} />
                {/* A grading-failure stub has no feedback rows; without this it
                    rendered as an empty "review" with two blank columns. */}
                {(selectedAssessment.gradingStatus === 'failed' || selectedAssessment.gradingStatus === 'permanently_failed') && (
                    <div style={{
                        padding: '14px 18px', marginBottom: '16px',
                        background: '#fef2f2', borderLeft: '4px solid #db2828',
                        borderRadius: '4px', fontSize: '14px', lineHeight: '1.6', color: '#7f1d1d',
                    }}>
                        {selectedAssessment.gradingStatus === 'failed'
                            ? 'Murphy hit a snag grading this practice, but your recording is safe and Murphy is retrying in the background. We’ll email you as soon as your feedback is ready.'
                            : 'Murphy was unable to grade this practice after several attempts. Your recording is saved; please record a new practice, and contact support if this keeps happening.'}
                    </div>
                )}
                {!(selectedAssessment.gradingStatus === 'failed' || selectedAssessment.gradingStatus === 'permanently_failed') && (
                <div style={{ display: 'flex', gap: '24px' }}>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>Positives</h3>
                        {selectedAssessment.positivesView.map((positive, i) =>
                            <div key={`positive_${i}`} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                padding: '12px 16px', marginBottom: '8px',
                                background: '#f0fdf4', borderLeft: '4px solid #21ba45',
                                borderRadius: '4px', fontSize: '14px', lineHeight: '1.6', color: '#1e293b',
                            }}>
                                <span style={{ color: '#21ba45', flexShrink: 0, marginTop: '2px' }}>&#10003;</span>
                                <span>{positive}</span>
                            </div>
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>Areas For Improvement</h3>
                        {selectedAssessment.areasForImprovementView.map((negative, i) =>
                            <div key={`negative${i}`} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                padding: '12px 16px', marginBottom: '8px',
                                background: '#fef2f2', borderLeft: '4px solid #db2828',
                                borderRadius: '4px', fontSize: '14px', lineHeight: '1.6', color: '#1e293b',
                            }}>
                                <span style={{ color: '#db2828', flexShrink: 0, marginTop: '2px' }}>&#9888;</span>
                                <span>{negative}</span>
                            </div>
                        )}
                    </div>
                </div>
                )}
                {/* Neutral delivery note: the qa-family "Overall Delivery & Fluency"
                    read is out of the band and out of both columns (it is a read,
                    not a verdict), but its coaching is worth having (Toby, 2026-09-04). */}
                {!(selectedAssessment.gradingStatus === 'failed' || selectedAssessment.gradingStatus === 'permanently_failed')
                    && selectedAssessment.deliveryNotesView?.length > 0 && (
                    <div data-testid="delivery-note" style={{ marginTop: '16px' }}>
                        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>Delivery</h3>
                        {selectedAssessment.deliveryNotesView.map((note, i) =>
                            <div key={`delivery_${i}`} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                padding: '12px 16px', marginBottom: '8px',
                                background: '#f8fafc', borderLeft: '4px solid #64748b',
                                borderRadius: '4px', fontSize: '14px', lineHeight: '1.6', color: '#1e293b',
                            }}>
                                <span style={{ color: '#64748b', flexShrink: 0, marginTop: '2px' }}>&#9679;</span>
                                <span>{note}</span>
                            </div>
                        )}
                    </div>
                )}
                  </>
                )}

                {/* Per-question feedback for every question-driven type (see questionBreakdown.js) */}
                {!hideQuestionBreakdown && showsQuestionBreakdown(selectedAssessment.sessionType) && selectedAssessment.qaAnalysis?.questionAnalysis?.length > 0 && (
                    <div ref={questionBreakdownRef} style={{ marginTop: '32px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
                        <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>
                            Question Breakdown
                        </h3>

                        {selectedAssessment.qaAnalysis.questionAnalysis.map((qa, i) => {
                            const key = `${selectedAssessment.id}_${i}`;
                            const questionText = qa.question?.trim();
                            const alreadyInLibrary = existingQuestions.some(eq => eq.question?.trim() === questionText);
                            const isLiveMeeting = selectedAssessment.sessionType === 'Live Meeting';
                            const isSaved = savedQuestions[key] === 'saved' || alreadyInLibrary || (isLiveMeeting && qa.matchedFromLibrary === true);
                            const isSaving = savedQuestions[key] === 'saving';
                            const addLabel = isLiveMeeting ? 'Add to Q&A Library' : 'Add to Objection Handling';
                            const savedLabel = isLiveMeeting ? 'In Q&A Library' : 'Added to Objection Handling';

                            return (
                                <div key={`qa_detail_${i}`} style={{
                                    padding: '16px', marginBottom: '12px',
                                    background: '#f8fafc', border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>
                                        Q{i + 1}: {qa.question}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', marginBottom: '8px' }}>
                                        <strong>Actual:</strong>{' '}
                                        {qa.userResponse || qa.answer || <em style={{ color: '#94a3b8' }}>(no response captured)</em>}
                                    </div>
                                    {qa.keyPointsCovered?.length > 0 && (
                                        <div style={{ fontSize: '13px', color: '#16a34a', marginBottom: '4px' }}>
                                            &#10003; Covered: {qa.keyPointsCovered.join(', ')}
                                        </div>
                                    )}
                                    {qa.keyPointsMissed?.length > 0 && (
                                        <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '4px' }}>
                                            &#9888; Missed: {qa.keyPointsMissed.join(', ')}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
                                        {qa.pointsAwarded != null && showsQuestionPoints(selectedAssessment.sessionType) ? (
                                            <span style={{ fontSize: '13px', fontWeight: 600, color: qa.pointsAwarded / qa.maxPoints >= COVERED_RATIO ? '#16a34a' : qa.pointsAwarded / qa.maxPoints >= COVERED_RATIO - 0.05 ? '#ca8a04' : '#dc2626' }}>
                                                {qa.pointsAwarded}/{qa.maxPoints} points
                                            </span>
                                        ) : <span />}
                                        {onSaveToLibrary && canEditLibrary && canSaveQuestionToLibrary(selectedAssessment.sessionType) && (
                                            isSaved ? (
                                                <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>
                                                    &#10003; {savedLabel}
                                                </span>
                                            ) : (
                                                <button
                                                    disabled={isSaving}
                                                    onClick={async () => {
                                                        setSavedQuestions(prev => ({ ...prev, [key]: 'saving' }));
                                                        try {
                                                            await onSaveToLibrary([{
                                                                question: qa.question,
                                                                answer: qa.expectedAnswer || qa.answer || '',
                                                                related_to: selectedAssessment.sessionType
                                                            }]);
                                                            setSavedQuestions(prev => {
                                                                const updated = { ...prev, [key]: 'saved' };
                                                                assessments.forEach(a => {
                                                                    if (a.qaAnalysis?.questionAnalysis) {
                                                                        a.qaAnalysis.questionAnalysis.forEach((q2, j) => {
                                                                            if (q2.question === qa.question) {
                                                                                updated[`${a.id}_${j}`] = 'saved';
                                                                            }
                                                                        });
                                                                    }
                                                                });
                                                                return updated;
                                                            });
                                                        } catch (err) {
                                                            console.error('Failed to save question:', err);
                                                            setSavedQuestions(prev => ({ ...prev, [key]: undefined }));
                                                        }
                                                    }}
                                                    style={{
                                                        fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600,
                                                        padding: '6px 14px', borderRadius: '4px', border: '1px solid #0784a7',
                                                        backgroundColor: 'transparent', color: '#0784a7',
                                                        cursor: isSaving ? 'default' : 'pointer',
                                                    }}
                                                >
                                                    {isSaving ? 'Adding...' : addLabel}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {/* Transcript download for live-meeting assessments. The
                    transcript fetch needs myMeetingsStore + companyId, so
                    the section is only rendered when the parent threads them
                    through (NewAssessmentsReportPage does; older callers
                    don't, and silently skip). */}
                {!questionBreakdownOnly
                    && selectedAssessment.sessionType === 'Live Meeting'
                    && selectedAssessment.botId
                    && myMeetingsStore
                    && companyId && (
                    <TranscriptSection
                        meeting={{
                            status: 'delivered',
                            botId: selectedAssessment.botId,
                            meetingTitle: selectedAssessment.meetingTitle || 'Meeting Transcript',
                            meetingDate: selectedAssessment.meetingStartTime,
                            duration: selectedAssessment.duration,
                            participants: [],
                        }}
                        myMeetingsStore={myMeetingsStore}
                        companyId={companyId}
                    />
                )}
            </div>
        </>
    );
};

export default observer(NewAssessmentDetails);